import React from "react";
import PropTypes from "prop-types";
import { getColor } from "@gcloud-npm/utils.enums.colors";

let API = "{host}/api/v1/get-glyph";
const currentEnv = `${process.env.NEXT_PUBLIC_ENV_NAME}`;
const SSR_PIC_PROD = "https://ssr.col.movistar.es";
const SSR_PIC_DEV = "https://dev.ssr.col.movistar.es";
const PATH_GET_PIC = "/api/v3/get-pic";

const ICON_GLYPH_KINDS = ["s", "m", "l"];

export const ICON_GLYPH_KINDS_V1 = {
  S: "s",
  M: "m",
  L: "l",
};

const checkPropNull = (prop) => {
  return prop === null || prop === 0 || prop === "0" || prop === "";
};

const checkKind = (id = "") => {
  // Si no se proporciona un sufijo (kind), establecer el valor por defecto como "m"
  if (!id.includes("-")) {
    id += "-m";
  }
  const words = id.split("-");
  const validKinds = ICON_GLYPH_KINDS;
  const suffix = words.pop();
  if (validKinds.includes(suffix)) {
    return id;
  } else {
    return id + "-m";
  }
};

const buildId = (id = "", kind = "") => {
  let correctId = null;
  // Para los casos que viene el Id sin '-' y en camel case
  if (/[A-Z]/.test(id)) {
    correctId = id.split(/(?=[A-Z])/).join("-");
    correctId.toLowerCase();
  }
  if (kind && correctId) {
    id = `${correctId}-${kind}`;
  } else if (kind && !correctId) {
    id = `${id}-${kind}`;
  } else if (!kind && correctId) {
    id = checkKind(correctId);
  } else {
    id = checkKind(id);
  }

  return id;
};

const isSSRImage = (path) => {
  return path && path.startsWith("/static/pictures");
};

const getApiHost = (pathname, env) => {
  let API = null;
  // Caso productivo
  if (env === "prod") {
    API = `${SSR_PIC_PROD}${PATH_GET_PIC}`;
    if (isSSRImage(pathname)) {
      API = SSR_PIC_PROD;
    }
  } else {
    API = `${SSR_PIC_DEV}${PATH_GET_PIC}`;
    if (isSSRImage(pathname)) {
      API = SSR_PIC_DEV;
    }
  }
  return API;
};

/**
 * Componente que permite dibujar un icono
 */
const IconGlyph = (props) => {
  const { srcSvg } = props;
  let {
    id,
    color,
    className,
    aspectRatio = "",
    priority,
    kind,
    alt,
    title,
    cms,
    cmsHandler,
  } = props;
  let {
    width,
    height,
    insetPosition,
    dataCy,
    layoutSize,
    backgroundColor,
    circleLayout,
    circleRadius,
  } = props;
  try {
    if (width) {
      width = parseFloat(width);
    }
  } catch (e) {
    width = null;
  }
  try {
    if (height) {
      height = parseFloat(height);
    }
  } catch (e) {
    height = null;
  }

  const finalBackgroundColor = backgroundColor
    ? getColor(backgroundColor)
    : "transparent";
  const thereIsCircleLayout = circleLayout && parseInt(circleLayout, 10) > 0;
  const finalCircleRadius = circleRadius ? `${circleRadius}%` : "50%";
  try {
    aspectRatio = parseFloat(aspectRatio);
  } catch (e) {
    aspectRatio = null;
  }
  if (isNaN(aspectRatio)) aspectRatio = null;
  let finalCircleLayout = thereIsCircleLayout
    ? {
        borderRadius: finalCircleRadius,
        padding: `${circleLayout}px`,
        width: width + circleLayout * 2,
      }
    : {};
  const finalId = buildId(id, kind);
  if (!dataCy) {
    dataCy = `${id}`;
  }
  const size = {};
  let finalTitle = "";
  if (title) {
    finalTitle = title;
  }

  if (currentEnv !== "prod") {
    API = API.replace("{host}", "https://dev.ssr.col.movistar.es");
    //API = API.replace('{host}', 'https://ssr.col.movistar.es');
  } else {
    API = API.replace("{host}", "https://ssr.col.movistar.es");
  }

  // comportamiento legacy
  if (!aspectRatio) {
    /* Damos valor a la otra coordenada en caso de no indicarse */
    if (width && !height) {
      // Si no está definido el height se le da el valor del width
      size.height = width;
      if (!layoutSize || !layoutSize.height) {
        layoutSize = { ...layoutSize, width };
      }
    } else if (height && !width) {
      // Si no está definido el width se le da el valor del height
      size.width = "auto";
      if (!layoutSize || !layoutSize.width) {
        layoutSize = { ...layoutSize, height };
      }
    } else if (width && height) {
      // Si ambos están definidos y son diferentes, establecer ambos como el valor de width
      size.width = width;
      size.height = height;
      if (insetPosition) {
        layoutSize = { width, height };
      }
    }
  } else {
    // (w/h) = ap; h = w/ap
    if (width && !height) {
      height = (width / aspectRatio).toFixed(2);
      if ((!layoutSize || !layoutSize.height) && height) {
        if (!height.toFixed) {
          layoutSize = { ...layoutSize, height };
        }
      }
    }
    if (!width && height) {
      width = (aspectRatio * height).toFixed(2);
      if ((!layoutSize || !layoutSize.width) && width) {
        layoutSize = { ...layoutSize, width };
      }
    }

    if (insetPosition) {
      insetPosition = {
        position: "relative",
        ...insetPosition,
      };
    }
  }

  let loadingAttribute = "eager";
  let fetchPriorityAttribute = "high";
  if (!priority) {
    loadingAttribute = "lazy";
    fetchPriorityAttribute = "none";
  }

  let finalClassName = "svg-layout-size";
  if (className) {
    finalClassName = `${finalClassName} ${className}`;
  }

  if (cms && cmsHandler) {
    finalClassName = `${finalClassName} element-editable`;
  }

  const finalAlt = alt || "";
  // if (!finalAlt && finalId) {
  //   finalAlt = finalId
  // }

  // cms placeholder
  if (cms && (id === "" || id === "none" || checkPropNull(width))) {
    if (cmsHandler) {
      return (
        <div
          className={`${finalClassName} cms-icon-placeholder`}
          style={{
            ...(layoutSize || {}),
            ...finalCircleLayout,
            backgroundColor: finalBackgroundColor,
          }}
          onClick={() => cmsHandler({ ...props, name: "icon-glyph" })}
        >
          <img
            title={finalTitle}
            src={`${API}/soporte-tecnico-m/20/white`}
            width={20}
            height={20}
          />
        </div>
      );
    } else return null;
  }

  if (!id || id === "none") {
    return null;
  }

  let finalSrc = `${API}/${finalId}/${
    size.height || (height / 16).toFixed(2)
  }/${color}`;
  if (srcSvg) {
    finalSrc = `${getApiHost(srcSvg, currentEnv)}${srcSvg}`;
  }

  return (
    <div
      className={finalClassName}
      style={{
        ...(layoutSize || {}),
        ...finalCircleLayout,
        backgroundColor: finalBackgroundColor,
      }}
      onClick={
        cms && cmsHandler
          ? () => cmsHandler({ ...props, name: "icon-glyph" })
          : null
      }
    >
      <img
        aspectratio={aspectRatio}
        title={finalTitle}
        data-cy={dataCy}
        src={finalSrc}
        width={size.width || width}
        height={size.height || height}
        style={{
          ...insetPosition,
          height: `${size.height || height}px`,
          width: `${size.width || width}px`,
        }}
        alt={finalAlt}
        loading={loadingAttribute}
        fetchpriority={fetchPriorityAttribute}
      />
    </div>
  );
};

export default IconGlyph;

IconGlyph.propTypes = {
  /**
   * {
   *  "info": "Nombre de icono y trazo a mostrar.",
   *  "kind": "both",
   *  "beautifulName": "Id de icono"
   * }
   */
  id: PropTypes.string,
  /**
   * {
   *  "info": "Ruta del svg para logotipos, tiene mas peso que la propiedad id, si se utiliza establacer id a none, ejemplo ruta /content/dam/movistar/icons/canales-tv/telecinco.svg",
   *  "kind": "both",
   *  "beautifulName": "Ruta del svg (logotipos)"
   * }
   */
  srcSvg: PropTypes.string,
  /**
   * {
   *  "info": "Colores que tendrá el icono",
   *  "kind": "both",
   *  "beautifulName": "Colores para cada trazo"
   * }
   */
  color: PropTypes.string,
  /**
   * {
   *  "info": "Classnames que heredará el componente, sirve para aplicar estilos",
   *  "kind": "dev",
   *  "beautifulName": "ClassName"
   * }
   */
  className: PropTypes.string,
  /**
   * {
   *  "info": "Añade etiqueta dom para priorizar su carga, útil si dicho icono, está visible, en el aterrizaje a una página",
   *  "kind": "dev",
   *  "beautifulName": "Carga prioritaria"
   * }
   */
  priority: PropTypes.bool,
  /**
   * {
   *  "info": "Tipo de trazo del icono",
   *  "kind": "both",
   *  "beautifulName": "Trazo",
   *  "values": "./index.js:ICON_GLYPH_KINDS_V1"
   * }
   */
  kind: PropTypes.string,
  /**
   * {
   *  "info": "Texto alternativo del icono para accesibilidad",
   *  "kind": "dev",
   *  "beautifulName": "Alt"
   * }
   */
  alt: PropTypes.string,
  /**
   * {
   *  "info": "Ancho en píxeles del icono. Obligatorio excepto cuando se especifica el height o layoutSize.",
   *  "kind": "both",
   *  "beautifulName": "Ancho"
   * }
   */
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * {
   *  "info": "Alto en píxeles del icono. Obligatorio excepto cuando se especifica el width o layoutSize.",
   *  "kind": "dev",
   *  "beautifulName": "Altura"
   * }
   */
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * {
   *  "info": "Cadena que identifica inequívocamente el componente, para finalidades de testing",
   *  "kind": "dev",
   *  "beautifulName": "cyData"
   * }
   */
  dataCy: PropTypes.string,
  /**
   * {
   *  "info": "Colorea el lienzo, en principio invisible del icono.",
   *  "kind": "both",
   *  "beautifulName": "Color del Fondo"
   * }
   */
  backgroundColor: PropTypes.string,
  /**
   * {
   *  "info": "Redondea el lienzo, este aumentara la altura con el valor indicado, creando un contorno entre el icono y la circunferencia",
   *  "kind": "both",
   *  "beautifulName": "Redondear"
   * }
   */
  circleLayout: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * {
   *  "info": "Porcentaje de redondeo del borde del icono.",
   *  "kind": "both",
   *  "beautifulName": "Radio del borde"
   * }
   */
  circleRadius: PropTypes.number,
  /**
   * {
   *  "info": "Proporción del icono real, expresado en decimales ej:(1.44) w/h",
   *  "kind": "both",
   *  "beautifulName": "Aspect Ratio"
   * }
   */
  aspectRatio: PropTypes.number,
  /**
   * {
   *  "info": "Objeto que define al tamaño al lienzo que contiene dicho icono",
   *  "kind": "dev",
   *  "beautifulName": "Tamaño lienzo"
   * }
   */
  layoutSize: PropTypes.shape({
    w: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    h: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  /**
   * {
   *  "info": "Objeto con información sobre las propiedades necesarias (id, timestamp...) para editar los valores en orion.",
   *  "kind": "dev",
   *  "beautifulName": "CMS"
   * }
   */
  cms: PropTypes.object,
  /**
   * {
   *  "info": "Handler para eventos del CMS. Se ejecuta cuando se clicka en el componente desde el CMS.",
   *  "kind": "dev",
   *  "beautifulName": "CMS Handler"
   * }
   */
  cmsHandler: PropTypes.func,
};
